// Pro entitlement helper (CFO Grade is no-login → entitlements live in localStorage,
// scoped to one device. The unlock_token is verified against the backend on load.)

import { api } from "./api";

const STORAGE_KEY = "bankable_pro_v1";

function readRaw() {
  if (typeof window === "undefined") return null;
  try {
    const s = window.localStorage.getItem(STORAGE_KEY);
    if (!s) return null;
    const v = JSON.parse(s);
    if (!v || typeof v !== "object") return null;
    return v;
  } catch {
    return null;
  }
}

export function getStoredEntitlement() {
  const v = readRaw();
  if (!v) return null;
  if (v.expires_at) {
    const exp = Date.parse(v.expires_at);
    if (!Number.isFinite(exp) || exp <= Date.now()) return null;
  }
  return v;
}

// Returns the stored entitlement only if it's PAST its expires_at. Used by
// the report page to render the "Your Pro Pass expired — renew" banner so
// a churned customer sees a friendly nudge instead of a silent paywall.
export function getExpiredEntitlement() {
  const v = readRaw();
  if (!v || !v.expires_at) return null;
  const exp = Date.parse(v.expires_at);
  if (!Number.isFinite(exp)) return null;
  return exp <= Date.now() ? v : null;
}

export function getProToken() {
  // Returns the raw unlock_token for Pro-gated API calls (e.g. memo rewrites).
  // Null when no entitlement is stored or it's expired.
  const ent = getStoredEntitlement();
  return ent?.token || null;
}

export function saveEntitlement(ent) {
  if (typeof window === "undefined" || !ent) return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ent));
}

export function clearEntitlement() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
}

export function hasProEntitlement(ent = getStoredEntitlement()) {
  return !!ent && ent.scope === "pro" && !!ent.active;
}

export function hasPdfEntitlement(ent = getStoredEntitlement(), reportId = null) {
  if (!ent || !ent.active) return false;
  if (ent.scope === "pro") return true; // Pro covers PDF
  if (ent.scope === "pdf_single") {
    if (!reportId) return false;
    return !ent.report_id || ent.report_id === reportId;
  }
  return false;
}

export async function verifyEntitlement() {
  const stored = readRaw();
  if (!stored?.token) return null;
  try {
    const { data } = await api.get(`/payments/verify/${stored.token}`);
    if (data?.active) {
      const ent = { ...stored, ...data, active: true };
      saveEntitlement(ent);
      return ent;
    }
    // Server says inactive. Two cases worth preserving the local record:
    //   1. reason === "expired"  → row exists, past expires_at
    //   2. reason === "not_found" BUT local copy is already past expires_at
    //      (e.g., admin wiped the test ledger, or the row was deleted) —
    //      from the user's POV, they used to have Pro and it ran out.
    // In both cases we keep the local record (with active=false) so the
    // /report page can surface a friendly "renew" prompt instead of
    // silently scrubbing all trace of the prior purchase.
    const wasPastExpiry = (() => {
      if (!stored.expires_at) return false;
      const exp = Date.parse(stored.expires_at);
      return Number.isFinite(exp) && exp <= Date.now();
    })();
    if (data?.reason === "expired" || wasPastExpiry) {
      const expiredEnt = { ...stored, ...data, active: false };
      saveEntitlement(expiredEnt);
      return expiredEnt;
    }
    clearEntitlement();
    return null;
  } catch {
    return null;
  }
}

export async function fetchPackages() {
  const { data } = await api.get("/payments/packages");
  return data.packages || [];
}

export async function fetchConfig() {
  // Currently piggybacks on /payments/packages — that response includes the
  // email_enabled flag so we don't have to add a separate call.
  const { data } = await api.get("/payments/packages");
  return { emailEnabled: !!data.email_enabled };
}

export async function createCheckout({ packageId, reportId = null }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const { data } = await api.post("/payments/checkout", {
    package_id: packageId,
    origin_url: origin,
    report_id: reportId,
  });
  return data; // { url, session_id, package }
}

export async function pollCheckoutStatus(sessionId) {
  const { data } = await api.get(`/payments/status/${sessionId}`);
  return data;
}

export async function saveTokenForRecovery({ token, email }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const { data } = await api.post("/payments/save-token", {
    unlock_token: token,
    email,
    origin_url: origin,
  });
  return data; // { saved, email_sent, email_enabled, restore_url }
}

export async function requestRecoveryEmail({ email }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const { data } = await api.post("/payments/recover", { email, origin_url: origin });
  return data; // { ok, email_enabled, message }
}

// Recover a saved report from the backend given a paid unlock_token. Used
// when sessionStorage has been cleared between analysis and payment (the
// classic "different device" or "incognito mode" footgun).
export async function fetchReportByToken(reportId, token) {
  if (!reportId || !token) return null;
  try {
    const { data } = await api.get(
      `/reports/${encodeURIComponent(reportId)}`,
      { params: { token } }
    );
    return data; // { report, entitlement }
  } catch {
    return null;
  }
}

// Build the absolute URL for the PDF download endpoint. The browser does
// the streaming; we don't pull the blob into JS memory because the file
// is small (<200KB typical) and a direct GET kicks off the OS "Save As"
// dialog naturally via `Content-Disposition: attachment`.
export function buildPdfDownloadUrl(reportId, token) {
  if (!reportId || !token) return null;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  return `${base}/api/reports/${encodeURIComponent(reportId)}/pdf?token=${encodeURIComponent(token)}`;
}
